import React, { useState, useEffect } from 'react';

const DaycareEnrollmentsModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  children, 
  academicYear, 
  apiCall,
  showError,
  showSuccess 
}) => {
  const [step, setStep] = useState(1);
  const [selectedChild, setSelectedChild] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    childId: null,
    packageId: null,
    startDate: new Date().toISOString().split('T')[0],
    manualDiscountAmount: 0,
    manualDiscountReason: ''
  });

  // Filter to only show unenrolled children
  const unenrolledChildren = children.filter(child => 
    !child.EnrollmentStatus || child.EnrollmentStatus !== 'Active'
  );

  // Calculate child's age in months
  const calculateAgeInMonths = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    const months = (today.getFullYear() - birthDate.getFullYear()) * 12 + 
                   (today.getMonth() - birthDate.getMonth());
    return months;
  };

  // Load applicable packages when child is selected
  useEffect(() => {
    if (selectedChild) {
      loadPackagesForChild(selectedChild.ChildID);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChild]);

  const loadPackagesForChild = async (childId) => {
    setLoading(true);
    try {
      const data = await apiCall(`/daycare/packages/for-child/${childId}?academicYear=${academicYear}`);
      setPackages(data || []);
    } catch (error) {
      showError('Error loading packages: ' + error.message);
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChildSelect = (child) => {
    setSelectedChild(child);
    setFormData({ 
      ...formData, 
      childId: child.ChildID,
      familyId: child.FamilyID  // ✅ Add familyId
    });
    setStep(2);
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setFormData({ ...formData, packageId: pkg.PackageID });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.packageId) {
      showError('Please select a package');
      return;
    }

    setLoading(true);
    try {
      // Add academicYear to the payload
      const enrollmentData = {
        ...formData,
        academicYear: academicYear
      };
      
      await apiCall('/daycare/enrollments', {
        method: 'POST',
        body: JSON.stringify(enrollmentData)
      });
      
      showSuccess(`${selectedChild.FirstName} enrolled successfully!`);
      onSuccess();
      handleClose();
    } catch (error) {
      showError('Error creating enrollment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedChild(null);
    setSelectedPackage(null);
    setPackages([]);
    setFormData({
      childId: null,
      packageId: null,
      startDate: new Date().toISOString().split('T')[0],
      manualDiscountAmount: 0,
      manualDiscountReason: ''
    });
    onClose();
  };

  const goBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedPackage(null);
      setFormData({ ...formData, packageId: null });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">New Daycare Enrollment</h2>
            <p className="text-blue-100 text-sm mt-1">
              {step === 1 ? 'Step 1: Select Child' : `Step 2: Select Package for ${selectedChild?.FirstName}`}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-3 bg-gray-50 border-b">
          <div className="flex items-center justify-between max-w-md mx-auto">
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                1
              </div>
              <span className={`ml-2 text-sm font-medium ${step >= 1 ? 'text-blue-600' : 'text-gray-500'}`}>
                Child
              </span>
            </div>
            <div className={`flex-1 h-1 mx-4 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-600'
              }`}>
                2
              </div>
              <span className={`ml-2 text-sm font-medium ${step >= 2 ? 'text-blue-600' : 'text-gray-500'}`}>
                Package
              </span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* STEP 1: Select Child */}
          {step === 1 && (
            <div>
              <div className="mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Select a child to enroll
                </h3>
                <p className="text-sm text-gray-600">
                  Only showing children who are not currently enrolled in Daycare
                </p>
              </div>

              {unenrolledChildren.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <p className="text-gray-600 font-medium mb-2">All children are already enrolled</p>
                  <p className="text-sm text-gray-500">Add a new child or manage existing enrollments</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {unenrolledChildren.map((child) => {
                    const ageMonths = calculateAgeInMonths(child.DateOfBirth);
                    const ageYears = Math.floor(ageMonths / 12);
                    const remainingMonths = ageMonths % 12;
                    
                    return (
                      <button
                        key={child.ChildID}
                        onClick={() => handleChildSelect(child)}
                        className="text-left p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200 group"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-gray-900 text-lg group-hover:text-blue-700">
                              {child.FirstName} {child.LastName}
                            </p>
                            <p className="text-sm text-gray-600 mt-1">
                              Family: {child.FamilyID}
                            </p>
                            <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                {new Date(child.DateOfBirth).toLocaleDateString()}
                              </span>
                              <span className="flex items-center font-medium text-blue-600">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                {ageYears > 0 && `${ageYears}y `}{remainingMonths}m ({ageMonths} months)
                              </span>
                            </div>
                          </div>
                          <div className="ml-3">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Available
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 2: Select Package */}
          {step === 2 && (
            <div>
              <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Enrolling:</p>
                    <p className="font-semibold text-gray-900 text-lg">
                      {selectedChild?.FirstName} {selectedChild?.LastName}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Age: {calculateAgeInMonths(selectedChild?.DateOfBirth)} months
                    </p>
                  </div>
                  <button
                    onClick={goBack}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Change Child
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-4">Loading packages...</p>
                </div>
              ) : packages.length === 0 ? (
                <div className="text-center py-12 bg-yellow-50 rounded-lg border border-yellow-200">
                  <svg className="w-16 h-16 mx-auto text-yellow-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-yellow-800 font-medium mb-2">No suitable packages found</p>
                  <p className="text-sm text-yellow-700">There are no packages available for this child's age group</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {/* Package Selection */}
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">
                      Select a Package
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {packages.map((pkg) => (
                        <button
                          key={pkg.PackageID}
                          type="button"
                          onClick={() => handlePackageSelect(pkg)}
                          className={`text-left p-4 border-2 rounded-lg transition-all duration-200 ${
                            selectedPackage?.PackageID === pkg.PackageID
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{pkg.PackageName}</p>
                              <p className="text-xs text-gray-500 mt-1">{pkg.TimeSlot}</p>
                            </div>
                            {selectedPackage?.PackageID === pkg.PackageID && (
                              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          
                          <div className="space-y-1 text-sm">
                            <p className="text-gray-600">
                              <span className="font-medium">Age:</span> {pkg.MinAgeMonths}-{pkg.MaxAgeMonths} months
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Time:</span> {pkg.StartTime} - {pkg.EndTime}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-medium">Capacity:</span> {pkg.AvailableSpots}/{pkg.MaxCapacity} spots available
                            </p>
                          </div>

                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-2xl font-bold text-blue-600">
                              ${pkg.MonthlyRate.toFixed(2)}
                              <span className="text-sm font-normal text-gray-500">/month</span>
                            </p>
                          </div>

                          {pkg.HasAvailability === 0 && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                                Full - No spots available
                              </span>
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Enrollment Details */}
                  {selectedPackage && (
                    <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-800">Enrollment Details</h3>
                      
                      {/* Start Date */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={formData.startDate}
                          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>

                      {/* Optional Discount */}
                      <div className="border-t border-gray-300 pt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Manual Discount (Optional)
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Amount ($)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={selectedPackage.MonthlyRate}
                              value={formData.manualDiscountAmount}
                              onChange={(e) => setFormData({ ...formData, manualDiscountAmount: parseFloat(e.target.value) || 0 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Reason</label>
                            <input
                              type="text"
                              value={formData.manualDiscountReason}
                              onChange={(e) => setFormData({ ...formData, manualDiscountReason: e.target.value })}
                              placeholder="e.g., Sibling discount"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              disabled={!formData.manualDiscountAmount}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Cost Summary */}
                      <div className="border-t border-gray-300 pt-4">
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Monthly Rate:</span>
                            <span className="font-medium">${selectedPackage.MonthlyRate.toFixed(2)}</span>
                          </div>
                          {formData.manualDiscountAmount > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Discount:</span>
                              <span className="text-red-600 font-medium">-${formData.manualDiscountAmount.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-300">
                            <span>Final Monthly Cost:</span>
                            <span className="text-blue-600">
                              ${(selectedPackage.MonthlyRate - (formData.manualDiscountAmount || 0)).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-6 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={goBack}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    >
                      ← Back to Children
                    </button>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={!selectedPackage || loading}
                        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      >
                        {loading ? 'Enrolling...' : 'Enroll Child'}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DaycareEnrollmentsModal;